import React, { useState, useRef, useEffect, useContext } from "react";

import { useHistory } from "react-router-dom";
// import { format } from "date-fns";
import openSocket from "socket.io-client";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';

import { i18n } from "../../translate/i18n";
import useChats from "../../hooks/useChats";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import ChatListItem from "../ChatListItem";
import {format} from "date-fns";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
}));

const NotificationsChatPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const desktopNotificationsRef = useRef(new Map());

	// const { tickets } = useTickets({ withUnreadMessages: "true" });
	const { tickets: chats } = useChats({ withUnreadMessages: "true" });
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();

	const closeDesktopNotification = async chatId => {
		const tag = String(chatId);
		const desktopNotification = desktopNotificationsRef.current.get(tag);

		if (desktopNotification) {
			desktopNotification.close();
			desktopNotificationsRef.current.delete(tag);
		}

		if (!("serviceWorker" in navigator)) return;

		try {
			const registration = await navigator.serviceWorker.getRegistration();
			if (!registration) return;

			const serviceWorkerNotifications = await registration.getNotifications({ tag });
			serviceWorkerNotifications.forEach(notification => notification.close());
		} catch {
			return;
		}
	};

	// const historyRef = useRef(history);

	useEffect(() => {
		soundAlertRef.current = play;
	}, [play]);

	useEffect(() => {
		setNotifications(chats);
	}, [chats]);

	useEffect(() => {
		if (!("setAppBadge" in navigator)) return;

		const updateAppBadge = async () => {
			try {
				await navigator.setAppBadge(notifications.length);
			} catch {
				// O badge pode não estar disponível fora de uma PWA instalada.
			}
		};

		void updateAppBadge();
	}, [notifications.length]);

	useEffect(() => {
		const socket = openSocket(window.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL, { auth: { token: localStorage.getItem("socketToken") }});

		socket.on("connect", () => {
			socket.emit("joinNotification");
			socket.emit("joinChats", user.id);
		});

		socket.on("chat", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const chatIndex = prevState.findIndex(t => t.id === data.chatId);
					if (chatIndex !== -1) {
						prevState.splice(chatIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				void closeDesktopNotification(data.chatId);
			}
		});

		socket.on("unreadMessageChat", data => {
            if (data.action === "delete" && (data.chat.messagesUnread === 0 || (data.chat.isGroup && data.chat[`messagesUnreadBy${user?.id}`] === 0))) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.chat.id);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				void closeDesktopNotification(data.chat.id);
			}
		})

		socket.on("appMessageChat", data => {
			const userIsParticipant =
				data.chat.userId === user?.id ||
				data.chat.callingUserId === user?.id ||
				data.chat.users?.some(item => item.id === user?.id);

			if (
				data.action === "create" &&
				!data.message.read &&
				userIsParticipant
			) {
				if (data.message.senderId !== user.id && (data.chat.messagesUnread > 0 || (data.chat.isGroup && data.chat[`messagesUnreadBy${user?.id}`] > 0))) {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.chat.id);
						if (ticketIndex !== -1) {
							prevState[ticketIndex] = data.chat;
							return [...prevState];
						}
						return [data.chat, ...prevState];
					});
				}

				if (data.message.senderId === user?.id) return;

				void handleNotifications(data);
			}
		});

		return () => {
			socket.disconnect();
		};
		// eslint-disable-next-line
	}, [user]);

	const handleNotifications = async data => {
		const { message, chat } = data;

		const userName = data.chat.userId === user?.id
			? data.chat.callingUser?.name
			: data.chat.user?.name;
		const title = `${i18n.t("tickets.notification.message")} ${userName || "Chat interno"}`;
		const tag = String(chat.id);

		const options = {
			data: {
				title,
				url: `${window.location.origin}/tickets/0/${chat.id}`
			},
			body: `${message.body || "Nova mensagem"} - ${format(new Date(), "HH:mm")}`,
			icon: "/144.png",
			tag,
			renotify: true,
		};

		soundAlertRef.current?.();

		const notificationSupported = "Notification" in window;
		const serviceWorkerSupported = "serviceWorker" in navigator;
		const permission = notificationSupported ? Notification.permission : "unsupported";

		if (!notificationSupported || permission !== "granted") return;

		try {
			if (serviceWorkerSupported) {
				const registration = await navigator.serviceWorker.getRegistration();

				if (registration) {
					try {
						await closeDesktopNotification(chat.id);
						await registration.showNotification(title, options);
						return;
					} catch {
						// Usa a API Notification como fallback.
					}
				}
			}

			const previousNotification = desktopNotificationsRef.current.get(tag);
			previousNotification?.close();

			const notification = new Notification(title, options);
			desktopNotificationsRef.current.set(tag, notification);

			notification.onclick = () => {
				window.focus();
				history.push(`/tickets/0/${chat.id}`);
				notification.close();
			};

			notification.onclose = () => {
				desktopNotificationsRef.current.delete(tag);
			};
		} catch {
			return;
		}
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
			>
				<Badge overlap="rectangular" badgeContent={notifications.length} color="secondary">
					<RecordVoiceOverIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<ChatListItem chat={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsChatPopOver;
